/**
 * Pure helpers for the floating-bar Duplicate menu (Workplace post
 * 1633835408373756): current-variant mode collapses every variant history on the
 * cloned node data to the picked entry (stored as V1), all-history mode keeps the
 * full clone. Both name the copy `<original> Copy - <YYYYMMDDHHmm>`.
 *
 * Every `*WithHistory` / asset-ref def in `schemas/defs.json` shares the
 * `{history: [], picked_index}` shape (video adds `picked_variant_id`), so the
 * collapse is structural over `out_*` fields rather than a per-type table.
 * Variant state that lives outside that shape, such as script variants, is
 * handled explicitly below.
 */
import {
  FE_TYPE_TO_BACKEND,
  YDOC_NODE_FIELDS,
  type SceneTextDict,
  type ScreenplayResultSceneDict,
  type ScriptDialogueDict,
  type ScriptScreenplaySceneDict,
  type ScriptVariantDict,
  type VoiceProfileDict,
  type VoiceRoleDict,
} from '../../generated/nodeDataTypes'

export type DuplicateNodeMode = 'current-variant' | 'all-history'

export const DUPLICATE_NODE_MODES: readonly DuplicateNodeMode[] = ['current-variant', 'all-history']

export interface DuplicateNodeOptions {
  mode: DuplicateNodeMode
  /** Final display name for the copy (see `buildDuplicateNodeName`). */
  name: string
  /** Correlates the toolbar request with the canvas write in `nad trace --expand`. */
  traceId?: string
}

export function newDuplicateTraceId(): string {
  const suffix =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `node-duplicate:${suffix}`
}

export interface CollapsedHistoryField {
  /** Dotted path from the node data root, e.g. `out_image.images.main`. */
  path: string
  historyBefore: number
  pickedIndexBefore: number
  historyAfter: number
}

export interface CollapseReport {
  fields: CollapsedHistoryField[]
  scriptVariantsBefore?: number
  scriptVariantsAfter?: number
  selectedScriptVariant?: Pick<ScriptVariantDict, 'id' | 'ordinal' | 'model'>
  droppedTakeSelections: number
  droppedAudioWorkspaces: number
  droppedVoiceProfiles: number
  clearedFields: string[]
}

/**
 * Flat, log-safe view of a collapse report — the NAD logger summarizes arrays
 * to `[n items]`, so the per-field detail is rendered as strings here.
 * e.g. `out_image: 3→1 (kept V2 as V1)`.
 */
export function summarizeCollapseReport(report: CollapseReport): Record<string, string | number> {
  const summary: Record<string, string | number> = {
    collapsedFieldCount: report.fields.length,
    collapsedFields:
      report.fields
        .map((f) => `${f.path}: ${f.historyBefore}→${f.historyAfter} (kept V${f.pickedIndexBefore + 1} as V1)`)
        .join('; ') || 'none',
    droppedTakeSelections: report.droppedTakeSelections,
    droppedAudioWorkspaces: report.droppedAudioWorkspaces,
    droppedVoiceProfiles: report.droppedVoiceProfiles,
    clearedFields: report.clearedFields.join(',') || 'none',
  }
  if (report.scriptVariantsBefore !== undefined) {
    summary.scriptVariants = `${report.scriptVariantsBefore}→${report.scriptVariantsAfter ?? 0}`
  }
  if (report.selectedScriptVariant) {
    const variant = report.selectedScriptVariant
    summary.selectedScriptVariant = `${variant.id} (V${variant.ordinal}, ${variant.model})`
  }
  return summary
}

export class DuplicateNodeCollapseError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'DuplicateNodeCollapseError'
  }
}

type DataRecord = Record<string, unknown>

const COPIED_NODE_RUNTIME_FIELDS = ['error', 'progress', 'progressMessage'] as const

/**
 * Reset transient execution state at the frontend copy boundary.
 *
 * Copies keep reusable inputs, outputs and variant history, but they do not
 * inherit the source node's live execution. Return a new object so callers can
 * sanitize snapshots without mutating clipboard or ReactFlow source data.
 */
export function resetCopiedNodeRuntimeState<T extends object>(data: T): T {
  const next: DataRecord = Object.fromEntries(Object.entries(data))
  next.status = 'idle'
  for (const field of COPIED_NODE_RUNTIME_FIELDS) delete next[field]
  return next as T
}

function isRecord(value: unknown): value is DataRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

/** `<originalName> Copy - <YYYYMMDDHHmm>` in the caller's local timezone. */
export function buildDuplicateNodeName(originalName: string, now: Date): string {
  const stamp = `${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}${pad2(now.getHours())}${pad2(now.getMinutes())}`
  return `${originalName} Copy - ${stamp}`
}

/**
 * Data key that holds the node's display name. Schema-declared `in_name` /
 * `in_label` win; nodes without either (media uploads) use the frontend
 * `label` data key they already rename through.
 */
export function resolveDuplicateNameField(feType: string): 'in_name' | 'in_label' | 'label' {
  const backendType = FE_TYPE_TO_BACKEND[feType] ?? feType
  const fields = YDOC_NODE_FIELDS[backendType]
  if (fields?.in_name) return 'in_name'
  if (fields?.in_label) return 'in_label'
  return 'label'
}

const GROUP_NODE_TYPES = new Set(['nodeGroup', 'webtoonGroup', 'shortVideoGroup'])

function clearDuplicatedGroupMembership(feType: string, data: DataRecord, report: CollapseReport | null): void {
  if (!GROUP_NODE_TYPES.has(feType) || !('in_child_node_ids' in data)) return
  data.in_child_node_ids = []
  report?.clearedFields.push('in_child_node_ids')
}

interface HistoryShaped extends DataRecord {
  history: unknown[]
  picked_index?: number
  picked_variant_id?: string | null
}

function isHistoryShaped(value: unknown): value is HistoryShaped {
  return isRecord(value) && Array.isArray(value.history)
}

function resolvePickedIndex(value: HistoryShaped, path: string): number {
  const stableId = value.picked_variant_id
  if (typeof stableId === 'string' && stableId) {
    const stableIndex = value.history.findIndex(
      (entry) => isRecord(entry) && isRecord(entry.result) && entry.result.id === stableId,
    )
    if (stableIndex >= 0) return stableIndex
  }
  const picked = value.picked_index ?? 0
  if (!Number.isInteger(picked) || picked < 0 || picked >= value.history.length) {
    throw new DuplicateNodeCollapseError(
      `${path}: picked_index ${String(picked)} is out of range for ${value.history.length} variant(s)`,
    )
  }
  return picked
}

function collapseHistory(value: HistoryShaped, path: string, report: CollapseReport): HistoryShaped {
  if (value.history.length === 0) return value
  const pickedIndex = resolvePickedIndex(value, path)
  const picked = value.history[pickedIndex]
  report.fields.push({
    path,
    historyBefore: value.history.length,
    pickedIndexBefore: pickedIndex,
    historyAfter: 1,
  })
  const next: HistoryShaped = { ...value, history: [picked], picked_index: 0 }
  if ('picked_variant_id' in next) {
    const pickedVariantId = isRecord(picked) && isRecord(picked.result) ? picked.result.id : undefined
    if (typeof pickedVariantId === 'string' && pickedVariantId) next.picked_variant_id = pickedVariantId
    else delete next.picked_variant_id
  }
  return next
}

/** Walk plain objects; collapse every history-shaped value, never descend into one. */
function collapseValue(value: unknown, path: string, report: CollapseReport): unknown {
  if (isHistoryShaped(value)) return collapseHistory(value, path, report)
  if (!isRecord(value)) return value
  let changed = false
  const next: DataRecord = {}
  for (const [key, child] of Object.entries(value)) {
    const collapsed = collapseValue(child, `${path}.${key}`, report)
    if (collapsed !== child) changed = true
    next[key] = collapsed
  }
  return changed ? next : value
}

/** Drop retired Video Generator audio-authoring sidecar state from copies. */
function clearVideoAudioAuthoringSidecars(data: DataRecord, report: CollapseReport): void {
  const selections = data.in_audio_take_selections
  if (isRecord(selections)) {
    report.droppedTakeSelections += Object.keys(selections).length
    delete data.in_audio_take_selections
    report.clearedFields.push('in_audio_take_selections')
  }

  const workspaces = data.in_audio_variant_workspaces
  if (isRecord(workspaces)) {
    report.droppedAudioWorkspaces += Object.keys(workspaces).length
    delete data.in_audio_variant_workspaces
    report.clearedFields.push('in_audio_variant_workspaces')
  }

  if ('target_variant_path' in data) {
    delete data.target_variant_path
    report.clearedFields.push('target_variant_path')
  }
}

interface FlattenedScriptVariant {
  screenplay: ScriptScreenplaySceneDict[]
  dialogue: ScriptDialogueDict[]
  actionLines: SceneTextDict[]
  characters: string[]
  locations: string[]
  musicCues: SceneTextDict[]
  sfx: SceneTextDict[]
}

function canonicalSceneCharacters(scene: ScreenplayResultSceneDict): string[] {
  const canonical: string[] = []
  const seen = new Set<string>()
  const appendOnce = (raw: unknown) => {
    if (typeof raw !== 'string') return
    const name = raw.trim()
    const key = name.toLocaleLowerCase()
    if (!name || seen.has(key)) return
    seen.add(key)
    canonical.push(name)
  }

  for (const character of scene.characters ?? []) appendOnce(character)
  for (const entry of scene.entries ?? []) {
    const entryType = entry.type?.trim().toLowerCase()
    if (entryType === 'dialogue' || entryType === 'os') appendOnce(entry.character)
  }
  return canonical
}

function flattenScriptVariantScenes(scenes: ScreenplayResultSceneDict[]): FlattenedScriptVariant {
  const screenplay: ScriptScreenplaySceneDict[] = []
  const dialogue: ScriptDialogueDict[] = []
  const actionLines: SceneTextDict[] = []
  const characters = new Set<string>()
  const locations = new Set<string>()
  const musicCues: SceneTextDict[] = []
  const sfx: SceneTextDict[] = []

  for (const scene of scenes) {
    const sceneNumber = scene.scene_number
    const entries = scene.entries ?? []
    const sceneCharacters = canonicalSceneCharacters(scene)
    const screenplayEntries = entries.map((entry) =>
      Object.fromEntries(Object.entries(entry).filter((pair): pair is [string, string] => typeof pair[1] === 'string')),
    )
    screenplay.push({
      ...(typeof sceneNumber === 'number' ? { scene_number: sceneNumber } : {}),
      ...(typeof scene.location === 'string' ? { location: scene.location } : {}),
      ...(typeof scene.interior_exterior === 'string' ? { interior_exterior: scene.interior_exterior } : {}),
      characters: sceneCharacters,
      entries: screenplayEntries,
    })

    for (const character of sceneCharacters) characters.add(character)
    if (typeof scene.location === 'string' && scene.location) locations.add(scene.location)

    for (const entry of entries) {
      if (typeof entry.text !== 'string') continue
      const sceneText: SceneTextDict = {
        ...(typeof sceneNumber === 'number' ? { scene_number: sceneNumber } : {}),
        text: entry.text,
      }
      if (entry.type === 'action') actionLines.push(sceneText)
      else if (entry.type === 'music') musicCues.push(sceneText)
      else if (entry.type === 'sfx') sfx.push(sceneText)
      else if (entry.type === 'dialogue' || entry.type === 'os' || entry.type === 'voiceover') {
        dialogue.push({
          ...(typeof sceneNumber === 'number' ? { scene_number: sceneNumber } : {}),
          ...(typeof entry.character === 'string' ? { character: entry.character } : {}),
          ...(typeof entry.emotion === 'string' ? { emotion: entry.emotion } : {}),
          ...(typeof entry.voice_role_id === 'string' ? { voice_role_id: entry.voice_role_id } : {}),
          text: entry.text,
          type: entry.type,
        })
      }
    }
  }

  return {
    screenplay,
    dialogue,
    actionLines,
    characters: [...characters].sort(),
    locations: [...locations].sort(),
    musicCues,
    sfx,
  }
}

function clearDuplicateField(data: DataRecord, field: string, report: CollapseReport): void {
  if (!(field in data)) return
  delete data[field]
  report.clearedFields.push(field)
}

function scriptModelOutputField(model: string): 'out_gemini' | 'out_claude' | 'out_gpt' {
  if (model === 'gemini') return 'out_gemini'
  if (model === 'claude') return 'out_claude'
  if (model === 'gpt') return 'out_gpt'
  throw new DuplicateNodeCollapseError(`out_script_variants: unsupported selected model ${model}`)
}

function clearUnselectedScriptModelOutputs(
  data: DataRecord,
  selectedModelField: 'out_gemini' | 'out_claude' | 'out_gpt',
  report: CollapseReport,
): void {
  for (const field of ['out_gemini', 'out_claude', 'out_gpt'] as const) {
    if (field !== selectedModelField) clearDuplicateField(data, field, report)
  }
}

function deriveVoiceRoles(scenes: ScreenplayResultSceneDict[]): VoiceRoleDict[] {
  const roles = new Map<string, VoiceRoleDict>()
  for (const scene of scenes) {
    for (const entry of scene.entries ?? []) {
      const id = entry.voice_role_id
      if (typeof id !== 'string' || !id || roles.has(id)) continue
      roles.set(id, {
        id,
        label: typeof entry.character === 'string' && entry.character ? entry.character : id,
        kind: entry.type === 'voiceover' ? 'narrator' : 'character',
        ...(typeof entry.character === 'string' && entry.character
          ? { source_character: entry.character }
          : {}),
      })
    }
  }
  return [...roles.values()]
}

function resolveVoiceRoles(
  scenes: ScreenplayResultSceneDict[],
  selectedModelResult: DataRecord | undefined,
): VoiceRoleDict[] {
  const derived = deriveVoiceRoles(scenes)
  const stored = Array.isArray(selectedModelResult?.voice_roles)
    ? selectedModelResult.voice_roles.filter(
        (role): role is DataRecord => isRecord(role) && typeof role.id === 'string',
      )
    : []
  const storedById = new Map(stored.map((role) => [role.id as string, role]))
  return derived.map((role) => {
    const storedRole = typeof role.id === 'string' ? storedById.get(role.id) : undefined
    if (!storedRole) return role
    return {
      ...role,
      ...storedRole,
      // The selected screenplay owns identity and the displayed character;
      // the model result enriches it with kind / description metadata.
      id: role.id,
      label: role.label,
    } as VoiceRoleDict
  })
}

function filterVoiceProfiles(
  data: DataRecord,
  selectedRoleIds: ReadonlySet<string>,
  report: CollapseReport,
): void {
  if (!Array.isArray(data.out_voice_profiles)) return
  const profiles = data.out_voice_profiles
  const kept = profiles.filter(
    (profile): profile is VoiceProfileDict =>
      isRecord(profile) && typeof profile.name === 'string' && selectedRoleIds.has(profile.name),
  )
  report.droppedVoiceProfiles += profiles.length - kept.length
  data.out_voice_profiles = kept
}

/**
 * Make the selected history entry the copy's canonical Script Writer result.
 *
 * Merely shrinking `out_script_variants` is insufficient: ScriptWriterNode
 * renders the sole (master) entry from the top-level `out_*` fields. Leaving
 * those fields untouched therefore shows and exports the latest script even
 * when an older history entry was selected.
 */
function promoteScriptVariant(
  data: DataRecord,
  variant: ScriptVariantDict,
  report: CollapseReport,
): ScriptVariantDict {
  const selectedModelField = scriptModelOutputField(variant.model)
  const priorModelResult = isRecord(data[selectedModelField])
    ? data[selectedModelField]
    : undefined
  if (!Array.isArray(variant.screenplay)) {
    throw new DuplicateNodeCollapseError(
      `out_script_variants: selected variant ${variant.id} has no screenplay data to promote`,
    )
  }

  clearUnselectedScriptModelOutputs(data, selectedModelField, report)
  data.in_active_model = variant.model
  data.out_active_model = variant.model

  const scenes = variant.screenplay.map((scene) => ({
    ...scene,
    characters: canonicalSceneCharacters(scene),
  }))
  const flattened = flattenScriptVariantScenes(scenes)
  const voiceRoles = resolveVoiceRoles(scenes, priorModelResult)

  data.out_script = variant.script
  data.out_screenplay = flattened.screenplay
  data.out_dialogue = flattened.dialogue
  data.out_action_lines = flattened.actionLines
  data.out_characters = flattened.characters
  data.out_locations = flattened.locations
  data.out_music_cues = flattened.musicCues
  data.out_sfx = flattened.sfx
  data.out_voice_roles = voiceRoles
  data.out_scene_count = variant.scene_count ?? scenes.length
  data.out_logline = variant.logline ?? ''
  data.out_genre_tone = variant.genre_tone ?? ''
  data.out_synopsis = variant.synopsis ?? ''
  data.out_themes = variant.themes ?? ''
  clearDuplicateField(data, 'out_script_path', report)

  // ScriptVariantDict deliberately stores screenplay history, not a second
  // copy of model voice-role metadata. Enrich screenplay-derived identities
  // with matching stored role metadata while replacing variant-owned fields.
  const selectedModelResult: DataRecord = {
    voice_roles: voiceRoles,
    scenes,
  }
  for (const key of ['logline', 'genre_tone', 'synopsis', 'themes'] as const) {
    const value = variant[key]
    if (typeof value === 'string') selectedModelResult[key] = value
  }
  data[selectedModelField] = selectedModelResult
  filterVoiceProfiles(data, new Set(voiceRoles.map((role) => role.id).filter((id): id is string => Boolean(id))), report)
  return { ...variant, screenplay: scenes }
}

function isCanonicalScriptMaster(
  data: DataRecord,
  variants: unknown[],
  selected: ScriptVariantDict,
  activeId: unknown,
): boolean {
  // Legacy canvases without an explicit selection render `out_script`
  // directly, so a matching script is canonical even if history ordering is
  // incomplete.
  if ((typeof activeId !== 'string' || !activeId) && data.out_script === selected.script) return true

  const canonicalModel =
    typeof data.out_active_model === 'string'
      ? data.out_active_model
      : typeof data.in_active_model === 'string'
        ? data.in_active_model
        : undefined
  if (!canonicalModel) return false
  for (let index = variants.length - 1; index >= 0; index -= 1) {
    const candidate = variants[index]
    if (isRecord(candidate) && candidate.model === canonicalModel) {
      return candidate.id === selected.id
    }
  }
  return false
}

function preserveCanonicalScriptMaster(
  data: DataRecord,
  variant: ScriptVariantDict,
  report: CollapseReport,
): ScriptVariantDict {
  const selectedModelField = scriptModelOutputField(variant.model)
  const modelResult = isRecord(data[selectedModelField]) ? data[selectedModelField] : undefined
  const canonical: ScriptVariantDict = {
    ...variant,
    script: typeof data.out_script === 'string' ? data.out_script : variant.script,
  }

  if (Array.isArray(modelResult?.scenes)) {
    canonical.screenplay = modelResult.scenes as ScreenplayResultSceneDict[]
  } else if (Array.isArray(variant.screenplay)) {
    canonical.screenplay = variant.screenplay
  } else if (variant.screenplay !== undefined) {
    throw new DuplicateNodeCollapseError(
      `out_script_variants: selected variant ${variant.id} has invalid screenplay data`,
    )
  } else {
    delete canonical.screenplay
  }
  if (typeof data.out_scene_count === 'number') canonical.scene_count = data.out_scene_count
  for (const key of ['logline', 'genre_tone', 'synopsis', 'themes'] as const) {
    const value = data[`out_${key}`]
    if (typeof value === 'string') canonical[key] = value
  }

  clearUnselectedScriptModelOutputs(data, selectedModelField, report)
  if (canonical.script !== variant.script) clearDuplicateField(data, 'out_script_path', report)
  data.in_active_model = variant.model
  data.out_active_model = variant.model
  return canonical
}

function collapseScriptVariants(data: DataRecord, report: CollapseReport): void {
  const variants = data.out_script_variants
  if (!Array.isArray(variants) || variants.length === 0) return
  const activeId = data.in_active_variant_id
  let active = variants.find((v) => isRecord(v) && typeof activeId === 'string' && activeId && v.id === activeId)
  // Canvases that never set `in_active_variant_id` show `out_script` as master.
  if (!active && typeof data.out_script === 'string') {
    active = variants.find((v) => isRecord(v) && v.script === data.out_script)
  }
  if (!isRecord(active)) {
    throw new DuplicateNodeCollapseError(
      `out_script_variants: active variant ${String(activeId || '(unset)')} not found among ${variants.length} variant(s)`,
    )
  }
  if (
    typeof active.id !== 'string' ||
    typeof active.ordinal !== 'number' ||
    typeof active.model !== 'string' ||
    typeof active.script !== 'string'
  ) {
    throw new DuplicateNodeCollapseError('out_script_variants: selected variant is missing required fields')
  }
  const selected = active as unknown as ScriptVariantDict
  report.scriptVariantsBefore = variants.length
  report.scriptVariantsAfter = 1
  report.selectedScriptVariant = {
    id: selected.id,
    ordinal: selected.ordinal,
    model: selected.model,
  }
  const retained = isCanonicalScriptMaster(data, variants, selected, activeId)
    ? preserveCanonicalScriptMaster(data, selected, report)
    : promoteScriptVariant(data, selected, report)
  data.out_script_variants = [{ ...retained, ordinal: 1 }]
  data.in_active_variant_id = selected.id
}

/**
 * Collapse cloned node data so the picked variant becomes the only variant (V1).
 * `data` must already be a plain-JS clone (`ydocSafeCloneNodeData`); the input
 * object is not mutated. Throws `DuplicateNodeCollapseError` on an invariant
 * violation rather than guessing which variant the user meant.
 */
export function collapseNodeDataToPickedVariant(data: DataRecord): { data: DataRecord; report: CollapseReport } {
  const report: CollapseReport = {
    fields: [],
    droppedTakeSelections: 0,
    droppedAudioWorkspaces: 0,
    droppedVoiceProfiles: 0,
    clearedFields: [],
  }
  const next: DataRecord = { ...data }
  for (const [key, value] of Object.entries(data)) {
    if (!key.startsWith('out_')) continue
    next[key] = collapseValue(value, key, report)
  }
  collapseScriptVariants(next, report)
  clearVideoAudioAuthoringSidecars(next, report)
  // Feedback rounds describe how earlier variants were produced.
  if (Array.isArray(next.in_feedback_history) && next.in_feedback_history.length > 0) {
    next.in_feedback_history = []
    report.clearedFields.push('in_feedback_history')
  }
  return { data: next, report }
}

export interface PreparedDuplicateNodeData<T extends object = DataRecord> {
  data: T
  /** Null for all-history mode, which keeps every variant. */
  report: CollapseReport | null
  nameField: ReturnType<typeof resolveDuplicateNameField>
  nameAutogenCleared: boolean
}

/**
 * Apply the chosen duplicate mode and name to already-cloned node data. Throws
 * `DuplicateNodeCollapseError` (from the collapse) before anything is written.
 */
export function prepareDuplicateNodeData<T extends object>(
  feType: string,
  data: T,
  options: DuplicateNodeOptions,
): PreparedDuplicateNodeData<T> {
  const record: DataRecord = Object.fromEntries(Object.entries(data))
  const collapsed = options.mode === 'current-variant' ? collapseNodeDataToPickedVariant(record) : null
  const nameField = resolveDuplicateNameField(feType)
  const next = resetCopiedNodeRuntimeState<DataRecord>({
    ...(collapsed?.data ?? record),
    [nameField]: options.name,
  })
  clearDuplicatedGroupMembership(feType, next, collapsed?.report ?? null)
  // The localized-default marker would re-render "Panel N" over the new name.
  const nameAutogenCleared = 'name_autogen' in next
  delete next.name_autogen
  // Same node type and schema keys as the input (histories shortened, name
  // replaced), so narrow back to the caller's generated node-data type — the
  // same boundary as `ydocSafeCloneNodeData`.
  return { data: next as T, report: collapsed?.report ?? null, nameField, nameAutogenCleared }
}
